import React, { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { LoginCallback } from '@okta/okta-react';

import { useFeatureFlag } from '@/store/slices/api/featureFlagsApiSlice';
import { useDashboardFeature } from '@/hooks/useNewDashboardFeature';
import AuthGuard from '@/routes/AuthGuard';

import ApplicationSettingsPage from '@/pages/ApplicationSettingsPage';
import LoadingPage from '@/pages/common/LoadingPage';
import LoadReport from '@/pages/physician/LoadReport';
import PatientListPage from '@/pages/physician/PatientListPage';
import PatientOverviewPage from '@/pages/physician/PatientOverviewPage';
import { TransmissionReportRedirectPage } from '@/pages/physician/TransmissionReportRedirectPage';

import routes from './config';

// Common Pages
const LoadingScreen = () => (
  <LoadingPage title="Loading" message="Please wait while we load your application..." />
);

const LoginRedirect = () => (
  <LoadingPage title="Redirecting" message="Logging you in, almost there..." />
);

// Physician Pages
const Dashboard = lazy(() => import('@/pages/physician/DashboardPage'));
const PatientsPage = lazy(() => import('@/pages/physician/PatientsPage'));
const PatientProfilePage = lazy(() => import('@/pages/physician/PatientProfilePage'));
const ReviewTransmissionReportsPage = lazy(
  () => import('@/pages/physician/ReviewTransmissionReportsPage')
);
const TransmissionReportPage = lazy(() => import('@/pages/physician/TransmissionReportPage'));
const InClinicPage = lazy(() => import('@/pages/physician/InClinicPage'));
const NewDashboard = lazy(() => import('@/pages/physician/NewDashboardPage'));

// Analytics Pages
const AnalyticsLayout = lazy(() => import('@/pages/physician/analytics/components/Layout'));
const AccountOverview = lazy(
  () => import('@/pages/physician/analytics/components/AccountOverview')
);
const AlertsPage = lazy(() => import('@/pages/physician/analytics/components/Alerts'));
const BillingPage = lazy(() => import('@/pages/physician/analytics/components/Billing'));
const SignatureStatusPage = lazy(
  () => import('@/pages/physician/analytics/components/SignatureStatus')
);
const LeadsRecallsPage = lazy(() => import('@/pages/physician/analytics/components/LeadsRecall'));
const DeviceRecallsPage = lazy(() => import('@/pages/physician/analytics/components/DeviceRecall'));
const BatteryLifePage = lazy(() => import('@/pages/physician/analytics/components/BatteryLife'));

// Authenticated Routes Component
const AuthenticatedRoutes = () => {
  const { getDashboardRoute, isNewDashboardEnabled } = useDashboardFeature();
  const isNewPatientProfilePageEnabled = useFeatureFlag('new_patient_profile_page_v2');

  return (
    <Routes>
      {/* Analytics Routes */}
      <Route path={routes.physician.analytics.root} element={<AnalyticsLayout />}>
        <Route
          index
          element={<Navigate to={routes.physician.analytics.accountOverview} replace />}
        />
        <Route path="account-overview" element={<AccountOverview />} />
        <Route path="alerts" element={<AlertsPage />} />
        <Route path="billing" element={<BillingPage />} />
        <Route path="signature-status" element={<SignatureStatusPage />} />
        <Route path="battery-life" element={<BatteryLifePage />} />
        <Route path="device-recalls" element={<DeviceRecallsPage />} />
        <Route path="leads-recalls" element={<LeadsRecallsPage />} />
      </Route>

      {/* Dashboard & Patient Routes */}
      {isNewDashboardEnabled ? (
        <Route path={routes.physician.dashboard} element={<NewDashboard />} />
      ) : (
        <Route path={routes.physician.dashboard} element={<Dashboard />} />
      )}
      <Route path={routes.physician.patients} element={<PatientsPage />} />
      <Route path={routes.physician.patientsList} element={<PatientListPage />} />
      <Route
        path={routes.physician.patientDetail(':uuid')}
        element={isNewPatientProfilePageEnabled ? <PatientOverviewPage /> : <PatientProfilePage />}
      />
      <Route path={routes.physician.inClinic} element={<InClinicPage />} />

      {/* Transmission Routes */}
      <Route
        path={routes.physician.reviewTransmissions}
        element={<ReviewTransmissionReportsPage />}
      />
      <Route
        path={routes.physician.reviewTransmission(':id')}
        element={<ReviewTransmissionReportsPage />}
      />
      <Route
        path={routes.physician.defaultTransmissionReport}
        element={<TransmissionReportRedirectPage />}
      />
      <Route
        path={routes.physician.transmissionReportDetail(':id')}
        element={<TransmissionReportPage />}
      />
      <Route path={routes.physician.editReport} element={<ReviewTransmissionReportsPage />} />
      <Route
        path={routes.physician.editReportById(':id')}
        element={<ReviewTransmissionReportsPage />}
      />
      <Route path={routes.physician.loadReport} element={<LoadReport />} />

      <Route path={routes.user.settings} element={<ApplicationSettingsPage />} />

      {/* Fallback Route */}
      <Route path="*" element={<Navigate to={getDashboardRoute()} replace />} />
    </Routes>
  );
};

const AppRoutes: React.FC = () => {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        {/* Public Routes - NOT wrapped in AuthGuard */}
        <Route
          path={routes.login.callback}
          element={<LoginCallback loadingElement={<LoginRedirect />} />}
        />

        {/* Explicit root redirect - NOT wrapped in AuthGuard */}
        <Route path={routes.root} element={<Navigate to="/physician/dashboard" replace />} />

        {/* Protected Routes - wrapped in AuthGuard */}
        <Route element={<AuthGuard />}>
          <Route path="/*" element={<AuthenticatedRoutes />} />
        </Route>
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
